import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { getChartColors } from '@/utils/chartColors';
import { getChartThemeColors, getChartPluginTheme } from '@/utils/chartTheme';
import useTheme, { resolveTheme } from '@/hooks/stores/useTheme';
import { BarChart2, PieChart, TrendingUp, ChevronDown, Table2 } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart', Icon: BarChart2 },
  { value: 'pie', label: 'Pie Chart', Icon: PieChart },
  { value: 'line', label: 'Line Chart', Icon: TrendingUp },
  { value: 'table', label: 'Table', Icon: Table2 },
];

// Generate a randomized color palette each time the chart is rendered
const getRandomizedColors = (count, brandColor) => {
  const baseColors = getChartColors(count, brandColor);
  const colors = [...baseColors];

  // Fisher–Yates shuffle for true random ordering
  for (let i = colors.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [colors[i], colors[j]] = [colors[j], colors[i]];
  }

  return colors;
};

/**
 * Reusable chart component with dropdown to switch between Bar, Pie, and Line.
 * @param {string} title - Chart title
 * @param {Array<{ label: string, count: number }>} data - Chart data
 * @param {string} [brandColor] - Optional brand color for series
 * @param {string} [className] - Optional wrapper class
 */
const ChartWithTypeSwitch = ({ title, data = [], brandColor = null, className = '' }) => {
  const [chartType, setChartType] = useState('bar');
  const themeMode = useTheme((s) => s.theme);
  const resolvedTheme = resolveTheme(themeMode);

  const chartTheme = useMemo(
    () => getChartThemeColors(),
    [resolvedTheme],
  );
  const pluginTheme = useMemo(
    () => getChartPluginTheme(chartTheme),
    [chartTheme],
  );

  const colors = useMemo(
    () => getRandomizedColors(data?.length ?? 0, brandColor),
    [data?.length, brandColor, title, resolvedTheme]
  );

  const hasNoData =
    !data ||
    data.length === 0 ||
    (data.length > 0 && data.every((d) => Number(d.count) === 0));

  if (hasNoData) {
    return (
      <div className={`py-8 text-center ${className}`}>
        <p className="text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  const labels = data.map((d) => `${d.label} (${d.count})`);
  const values = data.map((d) => d.count);
  const sliceBorder = chartTheme.card;

  const commonDataset = {
    bar: {
      labels,
      datasets: [
        {
          label: 'Background',
          data: values.map(() => Math.max(...values, 10) * 1.2), // Provide some headroom
          backgroundColor: 'hsl(var(--chart-inactive))',
          borderColor: 'hsl(var(--chart-inactive))',
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 32,
          grouped: false,
          order: 3,
        },
        {
          label: 'Secondary',
          data: values.map(v => v * 0.7),
          backgroundColor: chartTheme.isDark ? '#818CF8' : '#A78BFA',
          borderColor: chartTheme.isDark ? '#818CF8' : '#A78BFA',
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 32,
          grouped: false,
          order: 2,
        },
        {
          label: title,
          data: values,
          backgroundColor: chartTheme.isDark ? '#A78BFA' : '#7F56D9',
          borderColor: chartTheme.isDark ? '#A78BFA' : '#7F56D9',
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 32,
          grouped: false,
          order: 1,
        },
      ],
    },
    line: {
      labels,
      datasets: [
        {
          label: title,
          data: values,
          borderColor: chartTheme.isDark ? '#A78BFA' : '#7F56D9',
          borderWidth: 3,
          pointRadius: 0,
          tension: 0.4,
          fill: false,
          order: 1,
        },
        {
          label: 'Series 2',
          data: values.map(v => v * 0.8),
          borderColor: chartTheme.isDark ? '#818CF8' : '#9B8AFB',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
          fill: false,
          order: 2,
        },
        {
          label: 'Series 3',
          data: values.map(v => v * 0.6),
          borderColor: chartTheme.isDark ? '#C084FC' : '#D6BBFB',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
          fill: false,
          order: 3,
        }
      ],
    },
    pie: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: sliceBorder,
          borderWidth: 2,
          hoverOffset: 8,
        },
      ],
    },
  };

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: chartType === 'pie',
        position: 'right',
        labels: {
          ...pluginTheme.legend.labels,
          padding: 16,
          usePointStyle: true,
          ...(chartType === 'pie' && {
            generateLabels: (chart) => {
              const ds = chart.data.datasets[0];
              if (!ds || !chart.data.labels) return [];
              const total = ds.data.reduce((s, v) => s + v, 0);
              return chart.data.labels.map((label, i) => {
                const value = ds.data[i];
                const pct = total ? ((value / total) * 100).toFixed(1) : 0;
                const fill = Array.isArray(ds.backgroundColor)
                  ? ds.backgroundColor[i]
                  : ds.backgroundColor;
                return {
                  text: `${label} (${pct}%)`,
                  fillStyle: fill,
                  strokeStyle: sliceBorder,
                  fontColor: chartTheme.foreground,
                  lineWidth: 1,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i,
                };
              });
            },
          }),
        },
      },
      tooltip: {
        ...pluginTheme.tooltip,
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed ?? ctx.raw;
            if (chartType === 'pie') {
              const total = ctx.dataset.data.reduce((s, n) => s + n, 0);
              const pct = total ? ((v / total) * 100).toFixed(1) : 0;
              return `${ctx.label}: ${v} (${pct}%)`;
            }
            if (chartType === 'line') {
              const formatted =
                typeof v === 'number'
                  ? v
                  : ctx.formattedValue ?? (typeof v === 'object' ? JSON.stringify(v) : String(v));
              return `${ctx.label}: ${formatted}`;
            }
            const formatted =
              typeof v === 'number'
                ? v
                : ctx.formattedValue ?? (typeof v === 'object' ? JSON.stringify(v) : String(v));
            return `${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  };

  const barOptions = {
    ...commonOptions,
    scales: {
      x: {
        ticks: { maxRotation: 45, minRotation: 0, color: chartTheme.mutedForeground },
        grid: { color: chartTheme.grid },
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTheme.mutedForeground },
        grid: { color: chartTheme.grid },
      },
    },
  };

  const lineOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: { display: false },
    },
    scales: {
      x: {
        ticks: { maxRotation: 45, minRotation: 0, color: chartTheme.mutedForeground },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { display: true, color: chartTheme.mutedForeground },
        grid: { color: chartTheme.grid },
      },
    },
  };

  const doughnutOptions = {
    ...commonOptions,
    cutout: '75%',
  };

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return <Bar data={commonDataset.bar} options={barOptions} />;
      case 'line':
        return <Line data={commonDataset.line} options={lineOptions} />;
      case 'pie':
        return <Doughnut key={`pie-${resolvedTheme}`} data={commonDataset.pie} options={doughnutOptions} />;
      case 'table':
        return (
          <div className="h-full overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm text-left text-foreground">
              <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">Label</th>
                  <th className="px-4 py-3 font-semibold text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={`${row.label}-${idx}`} className="border-t border-border">
                    <td className="px-4 py-3">{row.label}</td>
                    <td className="px-4 py-3 text-right font-medium">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return <Bar data={commonDataset.bar} options={barOptions} />;
    }
  };

  return (
    <div className={`bg-card rounded-xl border border-border shadow-sm overflow-hidden ${className}`}>
      <div className="flex items-center justify-between gap-3 p-4 pb-2">
        <h3 className="text-lg font-semibold text-foreground truncate">{title}</h3>
        <div className="relative flex-shrink-0">
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            className="appearance-none pl-3 pr-9 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
            aria-label="Chart type"
          >
            {CHART_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
      </div>
      <div className="px-4 pb-4" style={{ height: '320px' }}>
        {renderChart()}
      </div>
    </div>
  );
};

export default ChartWithTypeSwitch;
